/**
 * State Hero Image Mapping - WebP Only
 *
 * Centralized configuration for state-specific hero images.
 * Uses modern WebP format for optimal performance.
 *
 * Images are located in app/assets/images/state-heroes/ and processed by webpack.
 * This ensures images go through CDN with proper cache headers.
 *
 * This follows the same pattern as PSP pages (e.g., RvStorageContent.js):
 * - Static imports resolved by webpack at build time
 * - URLs include publicPath prefix (/static/ in dev, CDN in prod)
 *
 * Browser Support:
 * - WebP: Chrome 32+, Edge 18+, Firefox 65+, Safari 14+, iOS 14+ (95%+ coverage)
 */

import STATE_HERO_IMAGES from './stateHeroImports';

/**
 * Valid state codes (all 50 US states)
 */
export const VALID_STATE_CODES = [
	'AK',
	'AL',
	'AR',
	'AZ',
	'CA',
	'CO',
	'CT',
	'DE',
	'FL',
	'GA',
	'HI',
	'IA',
	'ID',
	'IL',
	'IN',
	'KS',
	'KY',
	'LA',
	'MA',
	'MD',
	'ME',
	'MI',
	'MN',
	'MO',
	'MS',
	'MT',
	'NC',
	'ND',
	'NE',
	'NH',
	'NJ',
	'NM',
	'NV',
	'NY',
	'OH',
	'OK',
	'OR',
	'PA',
	'RI',
	'SC',
	'SD',
	'TN',
	'TX',
	'UT',
	'VA',
	'VT',
	'WA',
	'WI',
	'WV',
	'WY',
];

/**
 * Default fallback - null indicates use CSS gradient background
 */
export const defaultHeroImages = null;

/**
 * Get hero images for a state code
 * Returns webpack-resolved URLs (e.g., '/static/images/state-heroes/desktop/AL.webp')
 *
 * @param {string} stateCode - Two-letter state code (case-insensitive)
 * @returns {object|null} Image URLs { desktop, mobile } or null
 */
export const getStateHeroImages = (stateCode) => {
	if (!stateCode) {
		return defaultHeroImages;
	}

	const normalizedCode = stateCode.toUpperCase();

	if (!VALID_STATE_CODES.includes(normalizedCode)) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(
				`Invalid state code: ${stateCode}. Using default images.`,
			);
		}
		return defaultHeroImages;
	}

	return STATE_HERO_IMAGES[normalizedCode] || defaultHeroImages;
};

/**
 * Preload state images (for preloading multiple states)
 */
export const preloadStateImages = (stateCodes) => {
	return stateCodes.map((code) => getStateHeroImages(code));
};

/**
 * Get all state hero images (for sitemap or bulk operations)
 */
export const getAllStateHeroImages = () => {
	return VALID_STATE_CODES.reduce((acc, code) => {
		acc[code] = getStateHeroImages(code);
		return acc;
	}, {});
};
